import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ChevronDown, List, ArrowLeft, Captions, CaptionsOff, RotateCcw, Share2, Keyboard } from "lucide-react";
import { toast } from "sonner";
import { api, resolveMedia } from "@/lib/api";
import { getEpisodeProgress, saveProgress, clearProgress, watchPath } from "@/lib/progress";
import { RelatedAnime } from "@/components/RelatedAnime";
import { Comments } from "@/components/Comments";
import { AdUnit } from "@/components/AdUnit";
import { SeasonTabs, groupSeasons } from "@/components/SeasonTabs";
import { PlaybackError } from "@/components/PlaybackError";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export default function Watch() {
  const { id, ep } = useParams();
  const [params] = useSearchParams();
  const season = Number(params.get("s") || 1);
  const startAt = Number(params.get("t") || 0);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [anime, setAnime] = useState(null);
  const [subLang, setSubLang] = useState(() => localStorage.getItem("kp_sub_lang") ?? "");
  const [subMenu, setSubMenu] = useState(false);
  const [resumeAt, setResumeAt] = useState(null);
  const [listSeason, setListSeason] = useState(season);
  const [playErr, setPlayErr] = useState(null);

  useEffect(() => {
    api.get(`/anime/${id}`).then((r) => setAnime(r.data));
  }, [id]);
  useEffect(() => setListSeason(season), [season]);

  const current = useMemo(() => anime?.episodes.find((e) => String(e.number) === String(ep) && (e.season || 1) === season), [anime, ep, season]);
  const idx = anime?.episodes.indexOf(current) ?? -1;
  const prev = idx > 0 ? anime.episodes[idx - 1] : null;
  const next = idx >= 0 && idx < (anime?.episodes.length || 0) - 1 ? anime.episodes[idx + 1] : null;
  const seasons = anime ? groupSeasons(anime.episodes) : [];
  const sideEps = anime ? anime.episodes.filter((e) => (e.season || 1) === listSeason) : [];
  useEffect(() => setPlayErr(null), [current]);

  useEffect(() => {
    const saved = current ? getEpisodeProgress(anime.id, current.number, season) : null;
    setResumeAt(saved && saved.time > 5 && saved.time / saved.duration < 0.95 ? saved.time : null);
  }, [anime, current, season]);

  const subs = current?.subtitles || [];
  const activeSub = subs.find((s) => s.lang === subLang) || null;

  const applyTracks = () => {
    const v = videoRef.current;
    if (!v) return;
    for (const t of Array.from(v.textTracks || [])) t.mode = t.language === subLang ? "showing" : "hidden";
  };

  useEffect(() => {
    applyTracks();
  }, [subLang, current]);

  const chooseLang = (lang) => {
    setSubLang(lang);
    localStorage.setItem("kp_sub_lang", lang);
    setSubMenu(false);
  };

  const onLoaded = () => {
    const v = videoRef.current;
    if (v && startAt > 0) v.currentTime = startAt;
    else if (resumeAt && v) v.currentTime = resumeAt;
    applyTracks();
  };

  const share = async () => {
    const v = videoRef.current;
    const t = Math.floor(v?.currentTime || 0);
    const url = `${window.location.origin}${watchPath(anime.id, current.number, season)}${t > 5 ? `${season !== 1 ? "&" : "?"}t=${t}` : ""}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t > 5 ? `Link copied (starts at ${fmt(t)})` : "Episode link copied");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const [showKeys, setShowKeys] = useState(false);
  const goNext = () => next && navigate(watchPath(anime.id, next.number, next.season));
  const goPrev = () => prev && navigate(watchPath(anime.id, prev.number, prev.season));
  const nextRef = useRef(null);
  nextRef.current = { goNext, goPrev, subs, subLang, chooseLang: (l) => { setSubLang(l); localStorage.setItem("kp_sub_lang", l); }, share };

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.metaKey || e.ctrlKey || e.altKey) return;
      const v = videoRef.current;
      if (!v) return;
      const r = nextRef.current;
      const k = e.key.toLowerCase();
      if (e.code === "Space" || k === "k") { e.preventDefault(); v.paused ? v.play() : v.pause(); }
      else if (e.key === "ArrowRight" || k === "l") { e.preventDefault(); v.currentTime = Math.min(v.duration || Infinity, v.currentTime + 10); }
      else if (e.key === "ArrowLeft" || k === "j") { e.preventDefault(); v.currentTime = Math.max(0, v.currentTime - 10); }
      else if (e.key === "ArrowUp") { e.preventDefault(); v.volume = Math.min(1, v.volume + 0.1); }
      else if (e.key === "ArrowDown") { e.preventDefault(); v.volume = Math.max(0, v.volume - 0.1); }
      else if (k === "m") v.muted = !v.muted;
      else if (k === "f") document.fullscreenElement ? document.exitFullscreen() : v.requestFullscreen?.();
      else if (k === "n") r.goNext();
      else if (k === "p") r.goPrev();
      else if (k === "c" && r.subs.length) {
        const order = ["", ...r.subs.map((s) => s.lang)];
        r.chooseLang(order[(order.indexOf(r.subLang) + 1) % order.length]);
      } else if (k === "s") r.share();
      else if (k === "?") setShowKeys((x) => !x);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const lastSave = useRef(0);
  const onTime = () => {
    const v = videoRef.current;
    if (!v || !current || !v.duration) return;
    const now = Date.now();
    if (now - lastSave.current < 3000) return;
    lastSave.current = now;
    saveProgress({ animeId: anime.id, season, number: current.number, time: v.currentTime, duration: v.duration, animeTitle: anime.title, epTitle: current.title || `Episode ${current.number}`, cover: anime.banner_url || anime.cover_url });
  };

  const onEnded = () => {
    clearProgress(anime.id, current.number, season);
    if (next) navigate(watchPath(anime.id, next.number, next.season));
  };

  const restart = () => {
    const v = videoRef.current;
    if (v) v.currentTime = 0;
    clearProgress(anime.id, current.number, season);
    setResumeAt(null);
  };

  if (!anime) return <div className="min-h-screen" />;

  return (
    <main data-testid="watch-page" className="pt-24 sm:pt-28 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-9">
          <Link to={`/anime/${anime.id}`} data-testid="watch-back-link" className="kp-eyebrow flex items-center gap-2 mb-5 hover:!text-white transition-colors w-fit">
            <ArrowLeft size={14} /> {anime.title}
          </Link>

          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }} className="relative aspect-video bg-black border border-white/10 kp-glow-crimson overflow-hidden">
            {current ? (
              <video
                key={current.id}
                ref={videoRef}
                data-testid="mp4-video-player"
                src={resolveMedia(current.video_url)}
                poster={resolveMedia(anime.banner_url || anime.cover_url)}
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="w-full h-full"
                onLoadedMetadata={onLoaded}
                onTimeUpdate={onTime}
                onPause={onTime}
                onEnded={onEnded}
                onError={(e) => setPlayErr(e.currentTarget.error?.code ?? 4)}
              >
                {subs.map((s) => (
                  <track key={s.id} data-testid={`subtitle-track-${s.lang}`} kind="subtitles" label={s.label} srcLang={s.lang} src={resolveMedia(s.url)} default={s.lang === subLang} />
                ))}
              </video>
            ) : (
              <div data-testid="episode-not-found" className="absolute inset-0 flex items-center justify-center font-display">Episode not found</div>
            )}
            {current && playErr && <PlaybackError src={resolveMedia(current.video_url)} code={playErr} />}
            {resumeAt && !startAt && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="resume-banner" className="absolute top-4 left-4 kp-glass px-4 py-2.5 flex items-center gap-3 text-xs font-mono">
                <span className="text-[#00F0FF]">RESUMED AT {fmt(resumeAt)}</span>
                <button data-testid="restart-episode-button" onClick={restart} className="flex items-center gap-1 text-[#94A3B8] hover:text-white transition-colors"><RotateCcw size={12} /> Start over</button>
              </motion.div>
            )}
          </motion.div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6">
            <div>
              <span className="kp-eyebrow">{seasons.length > 1 ? `Season ${season} · ` : ""}Episode {current?.number}</span>
              <h1 data-testid="watch-episode-title" className="font-display font-bold text-2xl sm:text-3xl mt-2 tracking-tight">{current?.title || `Episode ${ep}`}</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <button
                  data-testid="subtitle-toggle-button"
                  onClick={() => setSubMenu(!subMenu)}
                  disabled={!subs.length}
                  title={subs.length ? "Subtitles" : "No subtitles for this episode"}
                  data-active={activeSub ? "cyan" : undefined}
                  className="kp-btn kp-btn-ghost !px-4 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {activeSub ? <Captions size={14} /> : <CaptionsOff size={14} />} CC {subs.length ? (activeSub ? activeSub.label : "Off") : "—"} {subs.length > 0 && <ChevronDown size={12} />}
                </button>
                {subMenu && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid="subtitle-menu" className="absolute right-0 bottom-full mb-2 z-20 min-w-[180px] kp-glass p-1.5 flex flex-col">
                    <button data-testid="subtitle-option-off" onClick={() => chooseLang("")} className={`text-left px-3 py-2 text-sm hover:bg-white/[0.06] transition-colors ${!activeSub ? "text-[#00F0FF]" : "text-[#94A3B8]"}`}>Off</button>
                    {subs.map((s) => (
                      <button key={s.id} data-testid={`subtitle-option-${s.lang}`} onClick={() => chooseLang(s.lang)} className={`text-left px-3 py-2 text-sm hover:bg-white/[0.06] transition-colors flex items-center justify-between gap-4 ${s.lang === subLang ? "text-[#00F0FF]" : "text-[#94A3B8]"}`}>
                        {s.label} <span className="font-mono text-[0.6rem] uppercase opacity-60">{s.lang}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </div>
              <button data-testid="share-episode-button" onClick={share} className="kp-btn kp-btn-ghost !px-4" title="Share link (S)">
                <Share2 size={14} /> Share
              </button>
              <button data-testid="prev-episode-button" disabled={!prev} onClick={goPrev} className="kp-btn kp-btn-ghost !px-4 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronLeft size={14} /> Prev
              </button>
              <button data-testid="next-episode-button" disabled={!next} onClick={goNext} className="kp-btn kp-btn-primary !px-4 disabled:opacity-30 disabled:cursor-not-allowed">
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
          <p className="text-[#94A3B8] text-sm leading-relaxed mt-6 max-w-3xl">{anime.synopsis}</p>

          <div className="mt-6">
            <button data-testid="keyboard-help-toggle" onClick={() => setShowKeys(!showKeys)} className="kp-eyebrow !text-[0.6rem] flex items-center gap-2 hover:!text-white transition-colors">
              <Keyboard size={13} /> Keyboard shortcuts {showKeys ? "hide" : "show"} <span className="opacity-60">(?)</span>
            </button>
            {showKeys && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid="keyboard-help" className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {[["Space / K", "Play / Pause"], ["← / →", "Seek 10s back / forward"], ["↑ / ↓", "Volume"], ["M", "Mute"], ["F", "Fullscreen"], ["N / P", "Next / Prev episode"], ["C", "Subtitles cycle"], ["S", "Share link"]].map(([k, d]) => (
                  <div key={k} className="kp-glass px-3 py-2 flex items-center justify-between gap-2">
                    <kbd className="font-mono text-[0.65rem] text-[#00F0FF]">{k}</kbd>
                    <span className="text-[#94A3B8] truncate">{d}</span>
                  </div>
                ))}
              </motion.div>
            )}
          </div>
          {current && <Comments episodeId={current.id} />}
        </div>

        <aside className="lg:col-span-3 flex flex-col gap-6">
          <div className="kp-glass p-5 lg:sticky lg:top-28">
            <div className="flex items-center gap-2 kp-eyebrow mb-4"><List size={13} /> Episodes · {sideEps.length}</div>
            <div className="mb-4"><SeasonTabs seasons={seasons} active={listSeason} onChange={setListSeason} testId="watch-season-tabs" /></div>
            <div data-testid="watch-episode-list" className="flex flex-col gap-1 max-h-[60vh] overflow-y-auto pr-1">
              {sideEps.map((e) => {
                const p = getEpisodeProgress(anime.id, e.number, e.season);
                const pct = p?.duration ? Math.min(100, Math.round((p.time / p.duration) * 100)) : 0;
                return (
                  <Link
                    key={e.id}
                    to={watchPath(anime.id, e.number, e.season)}
                    data-testid={`watch-episode-${e.number}`}
                    className={`relative flex items-center gap-3 px-3 py-3 text-sm transition-colors border-l-2 ${e.id === current?.id ? "border-[#FF2A55] bg-[#FF2A55]/10 text-white" : "border-transparent text-[#94A3B8] hover:bg-white/[0.04] hover:text-white"}`}
                  >
                    <span className="font-mono text-xs w-6">{String(e.number).padStart(2, "0")}</span>
                    {e.thumbnail_url && <img src={resolveMedia(e.thumbnail_url)} alt="" className="w-14 h-8 object-cover shrink-0" />}
                    <span className="truncate flex-1">{e.title || `Episode ${e.number}`}</span>
                    {e.subtitles?.length > 0 && <Captions size={12} className="text-[#00F0FF]" />}
                    {e.duration && <span className="font-mono text-[0.65rem] text-[#64748B]">{e.duration}</span>}
                    {pct > 0 && <span className="absolute bottom-0 left-0 h-[2px] bg-[#00F0FF]" style={{ width: `${pct}%` }} />}
                  </Link>
                );
              })}
            </div>
          </div>
        </aside>
      </div>
      <AdUnit testId="ad-unit-watch" className="mt-16" />
      <RelatedAnime animeId={anime.id} />
    </main>
  );
}
